import { Component } from 'react';

/**
 * Generic Error Boundary that prevents a single failing component
 * from unmounting the entire React tree (which causes a blank page).
 *
 * Usage:
 *   <ErrorBoundary fallback={<SomeFallback/>}>
 *     <PossiblyBuggyComponent />
 *   </ErrorBoundary>
 *
 * If no `fallback` is provided, a discreet placeholder is rendered so the
 * surrounding page keeps working.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.name || 'unnamed', error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      // Silent fallback: keep the layout alive without showing anything
      return null;
    }
    return this.props.children;
  }
}
